<template>
  <parent>
    <b-row class="justify-content-center">
      <b-col cols="12">
        <!-- Desktop header: unchanged from before the mobile work. -->
        <paper
          v-if="job"
          class="d-none d-lg-block"
        >
          <b-row
            class="mt-n2 px-2 py-1 justify-content-between align-items-center"
          >
            <b-col
              lg="auto"
              cols="12"
              class="mb-1 mb-lg-0"
            >
              <span
                class="text-thick"
                style="word-break:break-all;"
              >
                {{ job.name }}
              </span>
              <span class="ml-1">
                {{ job.status }}
              </span>
            </b-col>
            <b-col
              lg="auto"
              cols="12"
              class="d-flex justify-content-end flex-wrap"
            >
              <b-button
                variant="bg-white"
                :disabled="!job.isCancelable"
                class="mb-sm-0 mb-1"
                @click="job.isCancelable ? cancelJob() : ''"
              >
                <GjIcon
                  name="Close"
                  class="icon-left"
                />
                Cancel
              </b-button>
              <b-button
                variant="bg-white"
                class="ml-1 mb-sm-0 mb-1"
                :to="{ name: 'clone-job', params: { name: job.name } }"
              >
                <GjIcon
                  name="Copy"
                  class="icon-left"
                />
                Clone
              </b-button>
              <b-button
                v-if="!isEnded"
                variant="bg-white"
                class="ml-1 mb-sm-0 mb-1"
                @click="refreshDetails"
              >
                <GjIcon
                  name="Refresh"
                  class="icon-left"
                />
                Refresh
              </b-button>
            </b-col>
          </b-row>
        </paper>

        <!-- The layout hides the breadcrumb trail below 1200px, so mobile has no
             visible way back to the list - only the collapsed navbar title. -->
        <router-link
          :to="{ name: 'jobs' }"
          class="job-back d-lg-none"
        >
          <GjIcon
            name="ArrowLeft"
            size="18"
          />
          <span>Back to jobs</span>
        </router-link>

        <!-- Mobile header. PaperSimple, not Paper: Paper's body carries an `mt-2`
             utility that an `mt-n2` override wins over, which lifts the content
             out of the top of the card. -->
        <paper-simple
          v-if="job"
          card-class="mb-2 d-lg-none"
          body-class="p-0"
        >
          <div class="job-header">
            <div class="job-header__info">
              <job-status-badge
                :status="job.status"
                :in-flight="job.isCancelable"
              />
              <div
                class="job-header__title"
                :title="inputFilePath"
              >
                {{ fileName }}
              </div>
              <div class="job-header__id">
                <span class="job-header__id-value">{{ job.name }}</span>
                <a
                  v-clipboard:copy="job.name"
                  v-clipboard:success="copySuccess"
                  class="job-header__copy"
                  aria-label="Copy job ID"
                >
                  <GjIcon
                    name="Copy"
                    size="18"
                  />
                </a>
              </div>
            </div>

            <div class="job-header__actions">
              <b-button
                variant="bg-white"
                :disabled="!job.isCancelable"
                class="job-header__action"
                @click="job.isCancelable ? cancelJob() : ''"
              >
                <GjIcon
                  name="Close"
                  class="icon-left"
                />
                Cancel
              </b-button>
              <b-button
                variant="bg-white"
                class="job-header__action"
                :to="{ name: 'clone-job', params: { name: job.name } }"
              >
                <GjIcon
                  name="Copy"
                  class="icon-left"
                />
                Clone
              </b-button>
              <b-button
                v-if="!isEnded"
                variant="bg-white"
                class="job-header__action"
                @click="refreshDetails"
              >
                <GjIcon
                  name="Refresh"
                  class="icon-left"
                />
                Refresh
              </b-button>
            </div>
          </div>
        </paper-simple>

        <template v-if="job">
          <div :class="fetchingJob ? 'loading-blur' : ''">
            <Paper
              title="Overview"
              body-class="mt-0"
            >
              <div class="px-2">
                <b-row>
                  <b-col
                    cols="12"
                    sm="6"
                  >
                    <input-field
                      label="Status"
                      name="status"
                      label-position="top"
                    >
                      <div>
                        <job-status-badge
                          :status="job.status"
                          :in-flight="job.isCancelable"
                          class="d-lg-none"
                        />
                        <span class="d-none d-lg-inline">{{ job.status }}</span>
                      </div>
                    </input-field>
                  </b-col>
                  <b-col
                    cols="12"
                    sm="6"
                  >
                    <input-field
                      name="jobId"
                      label="Job ID"
                      label-position="top"
                    >
                      <a
                        v-clipboard:copy="job.name"
                        v-clipboard:success="copySuccess"
                        class="d-inline-flex align-items-center text-break"
                      >
                        {{ job.name }}
                        <GjIcon
                          name="Copy"
                          size="20"
                          class="ml-1 flex-shrink-0"
                        />
                      </a>
                    </input-field>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col
                    cols="12"
                    sm="6"
                  >
                    <input-field
                      name="submitTime"
                      label="Submit Time"
                      label-position="top"
                    >
                      <div>
                        {{ formatDateTime(job.insertDate) }}
                      </div>
                    </input-field>
                  </b-col>
                  <b-col
                    cols="12"
                    sm="6"
                  >
                    <input-field
                      label="Start time"
                      name="startTime"
                      label-position="top"
                    >
                      <div>
                        {{ formatDateTime(job.startDateTime) }}
                      </div>
                    </input-field>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col
                    cols="12"
                    sm="6"
                  >
                    <input-field
                      name="endTime"
                      label="End Time"
                      label-position="top"
                    >
                      <div>
                        {{ formatDateTime(job.endDateTime) }}
                      </div>
                    </input-field>
                  </b-col>
                  <b-col
                    cols="12"
                    sm="6"
                  >
                    <input-field
                      name="jobTemplate"
                      label="Job Template"
                      label-position="top"
                    >
                      <div>
                        {{ job.jobTemplateName || '-' }}
                      </div>
                    </input-field>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col
                    cols="12"
                    sm="6"
                  >
                    <input-field
                      label="Generate Thumbnail"
                      label-position="top"
                    >
                      <div>{{ job.generateThumbnail ? 'On' : 'Off' }}</div>
                    </input-field>
                  </b-col>
                </b-row>
              </div>
            </Paper>
            <collapsable
              v-if="drmContentId || drmKeyId"
              class="mb-2"
              card-class="border-none"
              header-class="p-0"
              title="DRM"
            >
              <template #header>
                <div class="text-thicker header-class">
                  DRM
                </div>
              </template>
              <div class="row">
                <input-field
                  class="col-12 col-sm-6"
                  label="DRM Content Id"
                  label-position="top"
                >
                  <div class="text-break">
                    {{ job.drmContentId || '-' }}
                  </div>
                </input-field>
                <input-field
                  class="col-12 col-sm-6"
                  label="DRM Key Id"
                  label-position="top"
                >
                  <div class="text-break">
                    {{ job.drmKeyId || '-' }}
                  </div>
                </input-field>
              </div>
            </collapsable>
            <collapsable
              card-class="border-none"
              header-class="p-0"
              class="mb-2"
              title="Tags"
            >
              <template #header>
                <div class="text-thicker header-class">
                  Tags
                </div>
              </template>

              <div class="d-lg-none">
                <div
                  v-if="tags.length"
                  class="job-tags"
                >
                  <div
                    v-for="(tag, index) in tags"
                    :key="index"
                    class="job-tags__item"
                  >
                    <span class="job-tags__key">{{ tag.key }}</span>
                    <span class="job-tags__value">{{ tag.value }}</span>
                  </div>
                </div>
                <div
                  v-else
                  class="job-tags__empty"
                >
                  No tags on this job.
                </div>
              </div>

              <div class="d-none d-lg-block">
                <b-table
                  ref="table"
                  :items="job.tags"
                  :fields="fields"
                  responsive
                  show-empty
                  class="loaded border-default border-b border-r border-l rounded"
                />
              </div>
            </collapsable>
            <collapsable
              ref="timingLogsCollapse"
              class="mb-2"
              card-class="border-none"
              header-class="p-0"
              title="Timing Logs"
            >
              <template #header>
                <div class="text-thicker header-class">
                  Timing Logs
                </div>
              </template>
              <template v-slot:default="{ visible }">
                <general-timing-logs
                  :visible="visible"
                  :is-ended="isEnded"
                />
              </template>
            </collapsable>
            <collapsable
              ref="inputcollapse"
              class="mb-2"
              card-class="border-none"
              header-class="p-0"
              title="Input"
            >
              <template #header>
                <div class="text-thicker header-class">
                  Input
                </div>
              </template>
              <input-summary
                :file-path="job.input.filePath"
                :destination="job.destination"
                :callback-url="job.input.callbackUrl"
                :s3AccountId="job.s3AccountId"
              />
            </collapsable>

            <collapsable
              ref="outputcollapse"
              class="mb-2"
              card-class="border-none"
              header-class="p-0"
              title="output"
              :is-visible="true"
            >
              <template #header>
                <div class="text-thicker header-class">
                  Output
                </div>
              </template>
              <output-summary
                :groups="job.groups"
                :destination="job.destination"
                :is-ended="isEnded"
              />
            </collapsable>
          </div>
        </template>
        <template v-if="fetchingJob">
          <div class="loading-div">
            <loading-page />
          </div>
          <div class="loading-backdrop" />
        </template>
      </b-col>
    </b-row>
  </parent>
</template>

<script>
import Paper from '@/components/paper/Paper.vue'
import Collapsable from '@/components/Collapsable.vue'
import InputSummary from './summary-helpers/InputSummary.vue'
import OutputSummary from './summary-helpers/OutputSummary.vue'
import GeneralTimingLogs from './summary-helpers/GeneralTimingLogs.vue'
import JobStatusBadge from './components/JobStatusBadge.vue'
import { isEndedStatus, splitFilePath } from './job-status'
import redirectError from '@/services/redirectError'

export default {
  name: 'JobSummary',
  components: {
    Paper,
    InputSummary,
    OutputSummary,
    GeneralTimingLogs,
    Collapsable,
    JobStatusBadge,
  },
  data() {
    return {
      fields: ['key', 'value'],
    }
  },
  computed: {
    job() {
      return this.$store.state.jobs.job
    },
    fetchingJob() {
      return this.$store.state.jobs.fetchingJob
    },
    isEnded() {
      return this.job ? isEndedStatus(this.job.status) : false
    },
    inputFilePath() {
      return this.job?.input?.filePath || ''
    },
    fileName() {
      return splitFilePath(this.inputFilePath).fileName || this.job?.name || ''
    },
    tags() {
      return this.job?.tags || []
    },
    drmContentId() {
      return this.job.drmContentId !== null && this.job.drmContentId !== ''
    },
    drmKeyId() {
      return this.job.drmKeyId !== null && this.job.drmKeyId !== ''
    },
  },
  created() {
    this.init()
  },
  beforeDestroy() {
    this.$store.dispatch('jobs/resetJob')
  },
  methods: {
    refreshDetails() {
      this.fetchJobByName()
    },
    init() {
      this.fetchJobByName()
    },
    fetchJobByName() {
      this.$store
        .dispatch('jobs/fetchJob', this.$route.params.name)
        .catch(err => redirectError(err, this.$router, this.$route.path))
    },
    cancelJob() {
      this.confirmDelete(
        'Cancel Job',
        'Are you sure you want to cancel this job?',
        'Confirm',
      ).then(ok => {
        if (ok) {
          this.$store
            .dispatch('jobs/cancelJob', this.job.name)
            .then(() => {
              this.successToast('Job canceled successfully')
              this.fetchJobByName()
            })
            .catch(error => {
              const message = error.response?.data?.Message
              this.errorToast(message || 'An error occurred.')
            })
        }
      })
    },
  },
}
</script>

<style>
.loading-div {
  position: absolute;
  top: calc(45vh - 123px);
  left: 50%;
  transform: translateX(-50%);
}
.loading-backdrop {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  background: rgba(0, 0, 0, 0.007);
}
.loading-blur {
  filter: blur(0.9px);
  transition: 200ms;
}
.header-class {
  padding: 16px 0;
}
</style>

<style lang="scss" scoped>
@import '@/assets/scss/variables/variables';

.job-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px; // comfortable tap target
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 500;
  color: $blue-800;

  &:hover,
  &:focus {
    color: $primary;
    text-decoration: none;
  }
}

// Mobile-only header (the desktop header keeps its original markup/styles).
.job-header {
  display: flex;
  flex-direction: column;
  padding: 16px;

  &__info {
    min-width: 0;
  }

  &__title {
    margin: 8px 0 0;
    font-size: 17px;
    font-weight: 700;
    line-height: 1.3;
    color: $blue-700;
    overflow-wrap: anywhere;
  }

  &__id {
    display: flex;
    align-items: center;
    margin-top: 2px;
  }

  &__id-value {
    min-width: 0;
    font-size: 12px;
    color: $blue-800;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__copy {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    color: $primary;
  }

  &__actions {
    display: flex;
    gap: 8px;
    margin-top: 16px;
  }

  &__action {
    flex: 1 1 0;
    justify-content: center;
    margin-right: 0 !important;
  }
}

.job-tags {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__item {
    display: flex;
    align-items: baseline;
    gap: 8px;
    padding: 10px 12px;
    border: 1px solid $gray-1234;
    border-radius: 6px;
    background-color: $gray-123;
  }

  &__key {
    flex: 0 0 auto;
    max-width: 45%;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: $blue-800;
    overflow-wrap: anywhere;
  }

  &__value {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 13px;
    color: $blue-700;
    overflow-wrap: anywhere;
  }

  &__empty {
    padding: 12px 0;
    font-size: 13px;
    color: $blue-800;
  }
}
</style>
